import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MobileContractSignPopup from './MobileContractSignPopup';
import { contractApi, type Contract } from '../../services/contractApi';
import { getTenantPayRoomCode } from '../../utils/tenantPayRoom';
import { getPropertyDisplayHeading } from '../../utils/propertyAddress';

type Props = {
  active?: boolean;
};

function apiErrorMessage(err: unknown, fallback: string): string {
  const axiosDetail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof axiosDetail === 'string' && axiosDetail.trim()) return axiosDetail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const MobileTenantContractPanel: React.FC<Props> = ({ active = false }) => {
  const { user } = useAuth();
  const roomCode = getTenantPayRoomCode(user?.id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const loadContract = useCallback(async () => {
    if (!roomCode) {
      setError('Không xác định được phòng. Hãy đăng xuất rồi quét lại QR cửa phòng.');
      setContract(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const detail = await contractApi.getTenantCurrentContract();
      setContract(detail);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Không tải được hợp đồng.'));
      setContract(null);
    } finally {
      setLoading(false);
    }
  }, [roomCode]);

  useEffect(() => {
    void loadContract();
  }, [loadContract]);

  useEffect(() => {
    if (active) {
      setPopupOpen(true);
      void loadContract();
    }
  }, [active, loadContract]);

  const heading = contract
    ? getPropertyDisplayHeading({
        title: contract.property_name,
        room_number: contract.room_number,
        full_address: contract.full_address,
      })
    : '';

  return (
    <section>
      <div className="mb-3 px-1">
        <h1 className="text-lg font-semibold text-black">Hợp đồng</h1>
        <p className="text-sm text-black/50">Nhấn để xem và lưu hợp đồng</p>
      </div>

      <button
        type="button"
        onClick={() => setPopupOpen(true)}
        className="w-full rounded-2xl bg-[#C4A35A] px-4 py-4 text-left shadow-sm"
      >
        <p className="text-base font-semibold text-white">Xem và lưu hợp đồng</p>
        <p className="mt-1 text-sm text-white/80">
          {contract
            ? `${heading}${contract.tenant_signature_image && contract.landlord_signature_image ? ' · Đã ký' : ' · Chủ nhà ký cho cả hai bên'}`
            : 'Mở popup để xem và lưu PDF'}
        </p>
      </button>

      <MobileContractSignPopup
        open={popupOpen}
        title="Hợp đồng"
        onClose={() => setPopupOpen(false)}
        contract={contract}
        loading={loading}
        error={error}
        showDownloadPdf
      />
    </section>
  );
};

export default MobileTenantContractPanel;
